import { zodResolver } from "@hookform/resolvers/zod";
import { Link, Mail, Search, UserPlus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SettingField } from "@/components/settings/setting-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSearchUsers } from "../hooks/use-invitations";
import type { TeamMember } from "../types";

// User search schema factory
const createUserSearchSchema = (t: any) =>
  z.object({
    searchQuery: z.string().min(1, t("teamManagement.searchByEmailName")),
  });

type UserSearchFormData = z.infer<ReturnType<typeof createUserSearchSchema>>;

interface LinkUserDialogProps {
  teamMember: TeamMember;
  isOpen: boolean;
  onClose: () => void;
  onLinkUser?: (workerId: string, userId: string) => Promise<void>;
  isLoading?: boolean;
}

// Search user type from GraphQL
interface SearchUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

export function LinkUserDialog({
  teamMember,
  isOpen,
  onClose,
  onLinkUser,
  isLoading = false,
}: LinkUserDialogProps) {
  const { t } = useTranslation("settings");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);

  // Use the real search hook
  const { data: searchData, loading: searching } = useSearchUsers(searchQuery, isOpen);
  const searchResults = searchData?.searchUsers?.users || [];

  const userSearchSchema = createUserSearchSchema(t);
  const form = useForm<UserSearchFormData>({
    resolver: zodResolver(userSearchSchema),
    defaultValues: {
      searchQuery: "",
    },
  });

  const onSearch = async (data: UserSearchFormData) => {
    setSearchQuery(data.searchQuery);
  };

  const handleLinkUser = async () => {
    if (!selectedUser || !onLinkUser) return;

    try {
      await onLinkUser(teamMember.id, selectedUser.id);
      onClose();
    } catch (error) {
      console.error("Failed to link user:", error);
    }
  };

  const getUserDisplayName = (user: SearchUser) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  // Reset search and selection when dialog opens/closes
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      setSearchQuery("");
      setSelectedUser(null);
      form.reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            {t("teamManagement.linkExistingUser", { name: teamMember.worker?.displayName ?? "" })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Status */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-100 text-orange-800">
                {t("teamManagement.noAccountLinked")}
              </Badge>
              <SettingField
                label={t("teamManagement.currentStatus")}
                helpText={t("teamManagement.workerExistsNoAccount")}
              />
            </div>
          </div>

          {/* Search Form */}
          <div className="space-y-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSearch)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="searchQuery"
                  render={({ field }) => (
                    <FormItem>
                      <SettingField
                        label={t("teamManagement.searchForExistingUser")}
                        helpText={t("teamManagement.searchByEmailName")}
                      >
                        <div className="flex gap-2">
                          <FormControl>
                            <Input
                              {...field}
                              type="text"
                              placeholder={t("teamManagement.enterEmailOrName")}
                              disabled={searching || isLoading}
                            />
                          </FormControl>
                          <Button
                            type="submit"
                            variant="outline"
                            disabled={searching || isLoading}
                            className="min-w-[100px]"
                          >
                            {searching ? (
                              t("teamManagement.searching")
                            ) : (
                              <>
                                <Search className="w-4 h-4 mr-2" />
                                {t("teamManagement.search")}
                              </>
                            )}
                          </Button>
                        </div>
                      </SettingField>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-4">
              <label className="text-sm font-medium">{t("teamManagement.searchResults")}</label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedUser?.id === user.id
                        ? "border-primary bg-primary/5"
                        : "border-border hover:bg-muted/50"
                    }`}
                    onClick={() => setSelectedUser(user)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="font-medium">{getUserDisplayName(user)}</div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                      {selectedUser?.id === user.id && (
                        <div className="text-primary">
                          <Link className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results Message */}
          {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
            <div className="text-center py-8 text-muted-foreground">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>{t("teamManagement.noUsersFound")}</p>
              <p className="text-sm">{t("teamManagement.tryDifferentSearch")}</p>
            </div>
          )}

          {/* Selected User Preview */}
          {selectedUser && (
            <div className="p-4 border rounded-lg bg-primary/5">
              <div className="space-y-2">
                <SettingField
                  label={t("teamManagement.selectedUser")}
                  helpText={t("teamManagement.linkUserDescription", {
                    workerName: teamMember.worker?.displayName ?? "",
                  })}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="font-medium">{getUserDisplayName(selectedUser)}</div>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {selectedUser.email}
                      </div>
                    </div>
                  </div>
                </SettingField>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t("actions.cancel")}
            </Button>
            <Button
              onClick={handleLinkUser}
              disabled={!selectedUser || isLoading}
              className="min-w-[120px]"
            >
              {isLoading ? (
                t("teamManagement.linking")
              ) : (
                <>
                  <Link className="w-4 h-4 mr-2" />
                  {t("teamManagement.linkUser")}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
